import { useMemo } from "react";
import { Text } from "@jsq/ds/core";
import { type ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { useGetCountriesQuery } from "~/graphql/supergraphHooks";
import { type GetProfileDetailsQuery } from "~/graphql/supergraphTypes";
import { buildCountryCodeToNameMap } from "~/pages/Compliance/CaseDetailsDrawer/Documents/TaxForms/utils/countryCodeToName";
import { ONBOARDING_CASE_TYPES } from "~/pages/Compliance/CaseDetailsDrawer/PaymentInstructions/constants";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useIOReusableProfiles, useIsFatcaCrsEnabledForCase } from "~/pages/Compliance/flags";
import * as styles from "./styles_vanilla.css";
import { TaxDocumentAccordion } from "./TaxDocumentAccordion";
import { filterTaxDocuments } from "./utils";

type ProfileDocument = NonNullable<
  GetProfileDetailsQuery["complianceProfile"]
>["documents"][number];

export type FatcaCrsDataSourceSectionProps = {
  documents: ProfileDocument[];
  caseId: number;
  profileId: number;
};

export const FatcaCrsDataSourceSection = ({
  documents,
  caseId,
  profileId,
}: FatcaCrsDataSourceSectionProps) => {
  const { caseType, fatcaCrsEnabled } = useCurrentCaseContext();
  const isFatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    caseType as ComplianceCaseTypeEnum | undefined,
    fatcaCrsEnabled,
  );

  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const scopeToCurrentCase =
    ioReusableProfilesEnabled &&
    caseType !== undefined &&
    ONBOARDING_CASE_TYPES.includes(caseType);

  const { data: countriesData } = useGetCountriesQuery();
  const countryCodeToName = useMemo(
    () => buildCountryCodeToNameMap(countriesData?.complianceCountries),
    [countriesData?.complianceCountries],
  );

  const taxDocuments = useMemo(() => filterTaxDocuments(documents), [documents]);

  if (!isFatcaCrsEnabled || taxDocuments.length === 0) {
    return null;
  }

  return (
    <div className={styles.sectionContainer}>
      <Text variant="h4" className={styles.sectionTitle}>
        FATCA/CRS data source assignment
      </Text>
      <div className={styles.accordionGroup}>
        {taxDocuments.map((document) => (
          <TaxDocumentAccordion
            key={document.id}
            document={document}
            caseId={caseId}
            profileId={profileId}
            countryCodeToName={countryCodeToName}
            scopeToCurrentCase={scopeToCurrentCase}
          />
        ))}
      </div>
    </div>
  );
};
